import { DeleteEventType, EditEventProps, UpdateEventPayload } from './types';

import { ZERO_TIME } from 'utils/constants';
import api from 'services/api';
import { convertDateWithTimezoneToUTC } from 'utils/utils';

export const updateEvent = ({ values, id }: EditEventProps) => {
	// start_date / end_date are schema-required, so they are non-null at submit; coerce defensively
	// because the form shape permits null (a missing/invalid inbound day seeds null, not an Invalid Date).
	const newDates = values.dates.map(({ start_date, end_date }) => ({
		start_date: start_date ? convertDateWithTimezoneToUTC(start_date, values.timezone) : '',
		end_date: end_date ? convertDateWithTimezoneToUTC(end_date, values.timezone) : '',
	}));

	const selling_time =
		values.selling_time === ZERO_TIME
			? null
			: convertDateWithTimezoneToUTC(new Date(values.selling_time ?? ''), values.timezone);

	const door_time = values.door_time ? convertDateWithTimezoneToUTC(new Date(values.door_time), values.timezone) : null;

	const custom_fees = values.custom_fees?.map(({ taxable, general, ...rest }) => ({
		...rest,
		taxable: taxable === '1',
		general: general === '1',
	}));

	const payload: UpdateEventPayload = {
		name: values.name,
		event_days: newDates,
		street_address: values.location,
		description: values.description,
		gate_time: values.gate_time === '' ? null : Number(values.gate_time),
		selling_time,
		postal_code: values.postal_code,
		ticketing_mode: values.ticketing_mode,
		custom_fees,
		custom_email_message: values.custom_email_message,
		support_contact: [{ contact: values.support }],
		time_zone: values.timezone,
		venue: values.venue,
		quantity: parseInt(values.quantity),
		approval_required: values.approval_required,
		seats_chart_key: values.seats_chart_key || null,
		enforce_no_orphan_seats: values.enforce_no_orphan_seats ?? false,
		door_time,
		presale_code: values.presale_code || null,
		always_show_tickets: values.always_show_tickets ?? false,
		on_sale: values.on_sale ?? true,
		inactive: values.inactive ?? false,
	};

	if (values.remove_password) {
		payload.password = null;
	} else if (values.want_to_change_password && values.password) {
		payload.password = values.password;
	} else if (!values.change_password && values.protect_with_password && values.password) {
		payload.password = values.password;
	}

	// hidden_until: false → send null to clear the restriction; true + date → send the date.
	if (values.hide_event && values.hidden_until) {
		payload.hidden_until = convertDateWithTimezoneToUTC(values.hidden_until, values.timezone);
	} else if (values.hide_event === false) {
		payload.hidden_until = null;
	}

	const queryParams = new URLSearchParams();
	if (values.adjust_ticket_dates !== undefined) {
		queryParams.append('adjust_ticket_dates', String(values.adjust_ticket_dates));
	}

	const url = `/events/${id}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
	return api.put(url, payload);
};

export const deleteEvent = ({ id }: DeleteEventType) => api.delete(`/events/${id}`);
